import { uiAppName } from '@nova/core';

export type CirceEnvVars = {
  ui_app_name: string;
  server_env: string;
  neutron_url: string;
  subspace_url: string;
  unleash_app_name: string;
  unleash_url: string;
  unleash_client_key: string;
  mixpanel_token?: string;
};

type CirceConfig = CirceEnvVars & {
  application_name: string;
  /**
   * Session Replay quota-protected knobs. These are hardcoded here and not derived from
   * VITE_* env vars so they cannot be accidentally switched on by an env file change.
   */
  mixpanel_session_replay_enabled: boolean;
  mixpanel_session_replay_sample_percent: number;
  mixpanel_session_replay_max_ms: number;
  mixpanel_session_replay_idle_timeout_ms: number;
};

// We enforce env vars here so it's consistent across all environments
function must<K extends keyof CirceEnvVars>(env: Partial<CirceEnvVars>, key: K): CirceEnvVars[K] {
  const envVarVal = env[key];
  if (!envVarVal) {
    throw new Error(`Missing env ${String(key)}`);
  }
  return envVarVal;
}

function getUnleashUrl(env: Partial<CirceEnvVars>) {
  const { unleash_url, neutron_url } = env;
  const url = unleash_url ? unleash_url.trim() : null;
  if (url) {
    return url;
  }
  return new URL('/feature-flag/proxy', neutron_url).toString();
}

function getUnleashClientKey(env: Partial<CirceEnvVars>) {
  const { unleash_client_key } = env;
  const key = unleash_client_key ? unleash_client_key.trim() : null;
  if (key) {
    return key;
  }
  const token =
    (sessionStorage?.getItem('access_token') || localStorage?.getItem('access_token')) ?? '';
  return `Bearer ${token || ''}`;
}

/**
 * Session Replay is billed against ONE 20k-recordings/month pool shared by every Loadsmart
 * Mixpanel project, and Mixpanel gives us no UI-side control — so the rate lives here,
 * hardcoded, and cannot be switched on by an env var.
 *
 * Production only. 'staging' was enabled for the initial QA window (OD-2496) and removed
 * here (OD-3992): staging draws from the same 20k-recordings/month pool. Re-enable it only
 * for a bounded QA window, and revert as soon as that window closes.
 */
const SESSION_REPLAY_ENVIRONMENTS = ['production'];

/** Production samples at 10%. Lower this if the shared pool projects over ~12k/month. */
const SESSION_REPLAY_SAMPLE_PERCENT: Record<string, number> = {
  production: 10
};

const formattedVars: Partial<CirceEnvVars> = Object.entries(import.meta.env).reduce(
  (env, [key, value]) => ({ ...env, [key.replace('VITE_', '').toLowerCase()]: value }),
  {} as Partial<CirceEnvVars>
);

const config: CirceConfig = {
  ...formattedVars,
  ...{
    ui_app_name: uiAppName.CIRCE,
    application_name: 'Opendock Nova',
    unleash_app_name: 'opendock-nova',
    unleash_url: getUnleashUrl(formattedVars),
    unleash_client_key: getUnleashClientKey(formattedVars),
    server_env: must(formattedVars, 'server_env'),
    neutron_url: must(formattedVars, 'neutron_url'),
    subspace_url: must(formattedVars, 'subspace_url'),
    // Mixpanel Session Replay — quota-protected knobs, intentionally not env-driven.
    mixpanel_session_replay_enabled: SESSION_REPLAY_ENVIRONMENTS.includes(
      formattedVars.server_env ?? ''
    ),
    mixpanel_session_replay_sample_percent:
      SESSION_REPLAY_SAMPLE_PERCENT[formattedVars.server_env ?? ''] ?? 0,
    mixpanel_session_replay_max_ms: 5 * 60 * 60 * 1000, // 5 hours
    mixpanel_session_replay_idle_timeout_ms: 60 * 60 * 1000 // 1 hour
  }
};

export default config;
